import { useLayoutEffect, useState } from 'react'
import type { RefObject } from 'react'

export type ClampMetrics = {
  /** True when the content is taller than `lines` and should collapse behind a toggle. */
  isClamped: boolean
  /** Height of `lines` lines, in px — the collapsed target. */
  collapsedHeight: number
  /** Natural full content height, in px — the expanded target. */
  fullHeight: number
}

/**
 * Measures whether a text element overflows `lines` lines and reports the
 * collapsed and full content heights. Re-measures when the element resizes.
 *
 * The first measurement runs before paint, so overflowing content starts in
 * its collapsed state without flashing at full height.
 */
export const useClampMetrics = (
  ref: RefObject<HTMLElement>,
  lines: number,
): ClampMetrics => {
  const [metrics, setMetrics] = useState<ClampMetrics>({
    isClamped: false,
    collapsedHeight: 0,
    fullHeight: 0,
  })

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return

    const measure = (): void => {
      // The measured element keeps its natural height inside the clipping
      // wrapper. Its child owns the typography and therefore the line-height.
      const typographyElement = element.firstElementChild ?? element
      const lineHeight = Number.parseFloat(
        window.getComputedStyle(typographyElement).lineHeight,
      )
      const fullHeight = element.scrollHeight

      // Fail open when line-height cannot be resolved: showing all content is
      // safer than clipping it to an invalid height with no usable disclosure.
      const hasLineHeight = Number.isFinite(lineHeight)
      const collapsedHeight = hasLineHeight ? lineHeight * lines : fullHeight

      const next = {
        isClamped: hasLineHeight && fullHeight - collapsedHeight > 1,
        collapsedHeight,
        fullHeight,
      }

      // A resize observer fires on every frame of a window drag, and most of
      // those frames reflow the text without changing its line count. Keeping
      // the previous object when nothing moved leaves those frames free of a
      // re-render, which matters because the inbox renders one of these per
      // comment, reply and caption with no virtualisation.
      setMetrics((previous) =>
        previous.isClamped === next.isClamped &&
        previous.collapsedHeight === next.collapsedHeight &&
        previous.fullHeight === next.fullHeight
          ? previous
          : next,
      )
    }

    measure()
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return (): void => observer.disconnect()
  }, [ref, lines])

  return metrics
}
